import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Preloader from '../components/Preloader';
import Sidebar from '../components/Sidebar';
import Header from '../components/Header';
import Footer from '../components/Footer';
import AlertBar from '../components/AlertBar';
import paymentService from '../services/paymentService';
import emailService from '../services/emailService';

const DonationPayment = () => {
  const navigate = useNavigate();
  const [donationData, setDonationData] = useState(null);
  const [alert, setAlert] = useState(null);

  // Payment states
  const [paymentMethod, setPaymentMethod] = useState('card');
  const [isProcessing, setIsProcessing] = useState(false);
  const [processingStatus, setProcessingStatus] = useState('');
  
  // Card payment state
  const [cardData, setCardData] = useState({
    cardName: '',
    cardNumber: '',
    expiryDate: '',
    cvv: '',
    billingAddress: '',
    city: '',
    state: '',
    zip: '',
    country: 'United States'
  });

  const [errors, setErrors] = useState({});
  const [cardFees, setCardFees] = useState(null);

  // Load donation data
  useEffect(() => {
    const savedData = sessionStorage.getItem('donationData');
    if (savedData) {
      const parsed = JSON.parse(savedData);
      setDonationData(parsed);
      
      // Calculate fees for selected processor
      const processor = parsed.processor || 'stripe';
      const fees = paymentService.calculateFees(parsed.amount, processor);
      setCardFees(fees);
    } else {
      navigate('/donations/setup');
    }
  }, [navigate]);

  // Format card number (spaces every 4 digits)
  const handleCardNumberChange = (e) => {
    let value = e.target.value.replace(/\D/g, '');
    let formatted = '';
    for (let i = 0; i < value.length; i++) {
      if (i > 0 && i % 4 === 0) formatted += ' ';
      formatted += value[i];
    }
    setCardData({ ...cardData, cardNumber: formatted });
    // Clear error
    if (errors.cardNumber) setErrors({ ...errors, cardNumber: '' });
  };

  // Format expiry date (MM/YY)
  const handleExpiryChange = (e) => {
    let value = e.target.value.replace(/\D/g, '');
    if (value.length >= 2) {
      value = value.substring(0, 2) + '/' + value.substring(2, 4);
    }
    setCardData({ ...cardData, expiryDate: value });
    if (errors.expiryDate) setErrors({ ...errors, expiryDate: '' });
  };

  // Format CVV (3-4 digits)
  const handleCVVChange = (e) => {
    const value = e.target.value.replace(/\D/g, '').substring(0, 4);
    setCardData({ ...cardData, cvv: value });
    if (errors.cvv) setErrors({ ...errors, cvv: '' });
  };

  // Validate payment form
  const validatePaymentForm = () => {
    const newErrors = {};

    if (paymentMethod === 'card') {
      if (!cardData.cardName.trim()) {
        newErrors.cardName = 'Name on card is required';
      }

      if (!cardData.cardNumber.trim()) {
        newErrors.cardNumber = 'Card number is required';
      } else if (!paymentService.validateCardNumber(cardData.cardNumber)) {
        newErrors.cardNumber = 'Invalid card number';
      }

      if (!cardData.expiryDate.trim()) {
        newErrors.expiryDate = 'Expiry date is required';
      } else if (!paymentService.validateExpiry(cardData.expiryDate)) {
        newErrors.expiryDate = 'Card is expired or invalid';
      }

      if (!cardData.cvv.trim()) {
        newErrors.cvv = 'CVV is required';
      } else if (!paymentService.validateCVV(cardData.cvv)) {
        newErrors.cvv = 'CVV must be 3-4 digits';
      }

      if (!cardData.billingAddress.trim()) {
        newErrors.billingAddress = 'Billing address is required';
      }
      if (!cardData.city.trim()) {
        newErrors.city = 'City is required';
      }
      if (!cardData.state.trim()) {
        newErrors.state = 'State is required';
      }
      if (!cardData.zip.trim()) {
        newErrors.zip = 'ZIP code is required';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Process payment
  const handlePayment = async () => {
    if (!validatePaymentForm()) {
      setAlert({
        type: 'danger',
        title: 'Validation Error',
        message: 'Please fix all errors before continuing',
        dismissible: true,
        autoHideDuration: 5000
      });
      return;
    }

    setIsProcessing(true);
    setProcessingStatus('Validating payment information...');

    try {
      let result;

      setProcessingStatus('Processing payment...');

      if (paymentMethod === 'card') {
        // Simulate Stripe payment processing
        result = await paymentService.processStripePayment(
          'pm_card_visa', // Mock payment method
          donationData.amount,
          donationData
        );
      } else if (paymentMethod === 'paypal') {
        // PayPal redirect
        result = await paymentService.processPayPalPayment(
          donationData.amount,
          donationData
        );
        
        if (result.success) {
          // Would redirect to PayPal in real implementation
          window.open(result.redirectUrl, '_blank');
        }
      } else if (paymentMethod === 'bank') {
        // Bank transfer setup
        result = await paymentService.initiateBankTransfer(
          donationData.amount,
          donationData
        );
      }

      if (result.success) {
        setProcessingStatus('Processing complete. Sending confirmation...');

        // Send confirmation emails
        await emailService.sendDonationConfirmationEmail(donationData, {
          amount: donationData.amount,
          transactionId: result.transactionId,
          receipt_url: result.receipt_url,
        });

        await emailService.sendTaxInformationEmail(
          donationData.donorEmail,
          donationData.donorName,
          {
            ein: '12-3456789',
            organizationName: 'Pathio Health Organization',
            taxStatus: '501(c)(3) Nonprofit',
          }
        );

        // If recurring donation
        if (donationData.frequency !== 'one-time') {
          await paymentService.setupRecurringDonation(
            donationData.amount,
            donationData.frequency,
            'pm_card_visa',
            donationData
          );

          await emailService.sendRecurringDonationEmail(
            donationData.donorEmail,
            donationData.donorName,
            {
              subscriptionId: `sub_${Date.now()}`,
              amount: donationData.amount,
              frequency: donationData.frequency,
              nextBillingDate: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000),
            }
          );
        }

        // Save to sessionStorage for confirmation page
        const confirmationData = {
          ...donationData,
          ...result,
          paymentMethod,
          timestamp: new Date().toISOString(),
        };

        sessionStorage.setItem('donationConfirmation', JSON.stringify(confirmationData));

        // Show success alert
        setAlert({
          type: 'success',
          title: 'Payment Successful!',
          message: 'Your donation has been processed successfully.',
          dismissible: true,
          autoHideDuration: 3000
        });

        // Navigate to confirmation page
        setTimeout(() => {
          navigate('/donations/confirmation');
        }, 1500);
      } else {
        // Payment failed
        setAlert({
          type: 'danger',
          title: 'Payment Failed',
          message: result.error || 'Unable to process payment. Please try again.',
          dismissible: true,
          autoHideDuration: 5000
        });

        // Send failure email
        await emailService.sendPaymentFailedEmail(
          donationData.donorEmail,
          donationData.donorName,
          {
            errorMessage: result.error,
            retryUrl: window.location.href,
          }
        );

        setIsProcessing(false);
        setProcessingStatus('');
      }
    } catch (error) {
      console.error('Payment error:', error);
      setAlert({
        type: 'danger',
        title: 'Error',
        message: error.message || 'An error occurred during payment processing.',
        dismissible: true,
        autoHideDuration: 5000
      });
      setIsProcessing(false);
      setProcessingStatus('');
    }
  };

  if (!donationData) {
    return <Preloader />;
  }

  const fees = cardFees || { total: donationData.amount, fees: 0 };

  return (
    <>
      <Header />
      <Sidebar />
      <main className="ul-main-content">
        {alert && (
          <AlertBar
            alert={alert}
            onDismiss={() => setAlert(null)}
          />
        )}

        <section className="ul-donation-page">
          <div className="container">
            <div className="row">
              {/* Left: Payment Form */}
              <div className="col-lg-8">
                <div className="ul-donation-form-wrapper">
                  {/* Progress Indicator */}
                  <div className="ul-progress-indicator">
                    <div className="ul-progress-step completed">
                      <span className="ul-step-number">1</span>
                      <span className="ul-step-label">Setup</span>
                    </div>
                    <div className="ul-progress-bar completed"></div>
                    <div className="ul-progress-step completed">
                      <span className="ul-step-number">2</span>
                      <span className="ul-step-label">Intent</span>
                    </div>
                    <div className="ul-progress-bar completed"></div>
                    <div className="ul-progress-step completed">
                      <span className="ul-step-number">3</span>
                      <span className="ul-step-label">Preferences</span>
                    </div>
                    <div className="ul-progress-bar active"></div>
                    <div className="ul-progress-step active">
                      <span className="ul-step-number">4</span>
                      <span className="ul-step-label">Payment</span>
                    </div>
                    <div className="ul-progress-bar"></div>
                    <div className="ul-progress-step">
                      <span className="ul-step-number">5</span>
                      <span className="ul-step-label">Confirm</span>
                    </div>
                  </div>

                  <h2 className="ul-form-title">Payment Information</h2>

                  {/* Payment Method Selection */}
                  <div className="ul-form-group">
                    <label className="ul-form-label">Payment Method</label>
                    <div className="ul-payment-methods">
                      <label className="ul-radio-option">
                        <input
                          type="radio"
                          value="card"
                          checked={paymentMethod === 'card'}
                          onChange={(e) => setPaymentMethod(e.target.value)}
                          disabled={isProcessing}
                        />
                        <span className="ul-radio-label">💳 Credit/Debit Card</span>
                      </label>
                      <label className="ul-radio-option">
                        <input
                          type="radio"
                          value="paypal"
                          checked={paymentMethod === 'paypal'}
                          onChange={(e) => setPaymentMethod(e.target.value)}
                          disabled={isProcessing}
                        />
                        <span className="ul-radio-label">🅿️ PayPal</span>
                      </label>
                      <label className="ul-radio-option">
                        <input
                          type="radio"
                          value="bank"
                          checked={paymentMethod === 'bank'}
                          onChange={(e) => setPaymentMethod(e.target.value)}
                          disabled={isProcessing}
                        />
                        <span className="ul-radio-label">🏦 Bank Transfer</span>
                      </label>
                    </div>
                  </div>

                  {/* Card Payment Form */}
                  {paymentMethod === 'card' && (
                    <div className="ul-card-form">
                      <h3 className="ul-subsection-title">Card Details</h3>

                      <div className="ul-form-group">
                        <label className="ul-form-label">Name on Card *</label>
                        <input
                          type="text"
                          className={`ul-form-control ${errors.cardName ? 'ul-is-invalid' : ''}`}
                          placeholder="John Doe"
                          value={cardData.cardName}
                          onChange={(e) => {
                            setCardData({ ...cardData, cardName: e.target.value });
                            if (errors.cardName) setErrors({ ...errors, cardName: '' });
                          }}
                          disabled={isProcessing}
                        />
                        {errors.cardName && (
                          <span className="ul-error-message">{errors.cardName}</span>
                        )}
                      </div>

                      <div className="ul-form-group">
                        <label className="ul-form-label">Card Number *</label>
                        <input
                          type="text"
                          className={`ul-form-control ${errors.cardNumber ? 'ul-is-invalid' : ''}`}
                          placeholder="4242 4242 4242 4242"
                          value={cardData.cardNumber}
                          onChange={handleCardNumberChange}
                          maxLength="19"
                          disabled={isProcessing}
                        />
                        {errors.cardNumber && (
                          <span className="ul-error-message">{errors.cardNumber}</span>
                        )}
                        <small className="ul-form-hint">Use 4242 4242 4242 4242 for testing</small>
                      </div>

                      <div className="row">
                        <div className="col-sm-6">
                          <div className="ul-form-group">
                            <label className="ul-form-label">Expiry Date (MM/YY) *</label>
                            <input
                              type="text"
                              className={`ul-form-control ${errors.expiryDate ? 'ul-is-invalid' : ''}`}
                              placeholder="12/25"
                              value={cardData.expiryDate}
                              onChange={handleExpiryChange}
                              maxLength="5"
                              disabled={isProcessing}
                            />
                            {errors.expiryDate && (
                              <span className="ul-error-message">{errors.expiryDate}</span>
                            )}
                          </div>
                        </div>
                        <div className="col-sm-6">
                          <div className="ul-form-group">
                            <label className="ul-form-label">CVV *</label>
                            <input
                              type="text"
                              className={`ul-form-control ${errors.cvv ? 'ul-is-invalid' : ''}`}
                              placeholder="123"
                              value={cardData.cvv}
                              onChange={handleCVVChange}
                              maxLength="4"
                              disabled={isProcessing}
                            />
                            {errors.cvv && (
                              <span className="ul-error-message">{errors.cvv}</span>
                            )}
                          </div>
                        </div>
                      </div>

                      <h3 className="ul-subsection-title">Billing Address</h3>

                      <div className="ul-form-group">
                        <label className="ul-form-label">Address *</label>
                        <input
                          type="text"
                          className={`ul-form-control ${errors.billingAddress ? 'ul-is-invalid' : ''}`}
                          placeholder="123 Main Street"
                          value={cardData.billingAddress}
                          onChange={(e) => {
                            setCardData({ ...cardData, billingAddress: e.target.value });
                            if (errors.billingAddress) setErrors({ ...errors, billingAddress: '' });
                          }}
                          disabled={isProcessing}
                        />
                        {errors.billingAddress && (
                          <span className="ul-error-message">{errors.billingAddress}</span>
                        )}
                      </div>

                      <div className="row">
                        <div className="col-sm-6">
                          <div className="ul-form-group">
                            <label className="ul-form-label">City *</label>
                            <input
                              type="text"
                              className={`ul-form-control ${errors.city ? 'ul-is-invalid' : ''}`}
                              placeholder="New York"
                              value={cardData.city}
                              onChange={(e) => {
                                setCardData({ ...cardData, city: e.target.value });
                                if (errors.city) setErrors({ ...errors, city: '' });
                              }}
                              disabled={isProcessing}
                            />
                            {errors.city && (
                              <span className="ul-error-message">{errors.city}</span>
                            )}
                          </div>
                        </div>
                        <div className="col-sm-6">
                          <div className="ul-form-group">
                            <label className="ul-form-label">State *</label>
                            <input
                              type="text"
                              className={`ul-form-control ${errors.state ? 'ul-is-invalid' : ''}`}
                              placeholder="NY"
                              value={cardData.state}
                              onChange={(e) => {
                                setCardData({ ...cardData, state: e.target.value });
                                if (errors.state) setErrors({ ...errors, state: '' });
                              }}
                              disabled={isProcessing}
                            />
                            {errors.state && (
                              <span className="ul-error-message">{errors.state}</span>
                            )}
                          </div>
                        </div>
                      </div>

                      <div className="row">
                        <div className="col-sm-6">
                          <div className="ul-form-group">
                            <label className="ul-form-label">ZIP Code *</label>
                            <input
                              type="text"
                              className={`ul-form-control ${errors.zip ? 'ul-is-invalid' : ''}`}
                              placeholder="10001"
                              value={cardData.zip}
                              onChange={(e) => {
                                setCardData({ ...cardData, zip: e.target.value });
                                if (errors.zip) setErrors({ ...errors, zip: '' });
                              }}
                              disabled={isProcessing}
                            />
                            {errors.zip && (
                              <span className="ul-error-message">{errors.zip}</span>
                            )}
                          </div>
                        </div>
                        <div className="col-sm-6">
                          <div className="ul-form-group">
                            <label className="ul-form-label">Country</label>
                            <select
                              className="ul-form-control"
                              value={cardData.country}
                              onChange={(e) => setCardData({ ...cardData, country: e.target.value })}
                              disabled={isProcessing}
                            >
                              <option>United States</option>
                              <option>Canada</option>
                              <option>United Kingdom</option>
                              <option>Australia</option>
                              <option>Other</option>
                            </select>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* PayPal Info */}
                  {paymentMethod === 'paypal' && (
                    <div className="ul-payment-info">
                      <div className="ul-info-box">
                        <p>✓ You will be securely redirected to PayPal to complete your donation.</p>
                        <p>✓ Your financial information is never shared with us.</p>
                        <p>✓ You'll receive a confirmation email after payment.</p>
                      </div>
                    </div>
                  )}

                  {/* Bank Transfer Info */}
                  {paymentMethod === 'bank' && (
                    <div className="ul-payment-info">
                      <div className="ul-info-box">
                        <h4>Bank Transfer Details</h4>
                        <p>Account Name: Pathio Health Organization</p>
                        <p>Account Number: ****1234</p>
                        <p>Routing Number: ****5678</p>
                        <p>Please allow 3-5 business days for processing.</p>
                      </div>
                    </div>
                  )}

                  {/* Security Notice */}
                  <div className="ul-security-notice">
                    <p>🔒 <strong>Secure Payment:</strong> All transactions are encrypted and secure.</p>
                    <p>✓ <strong>Tax Deductible:</strong> Your donation is tax-deductible under 501(c)(3).</p>
                  </div>

                  {/* Action Buttons */}
                  <div className="ul-form-actions">
                    <button
                      className="ul-btn ul-btn-secondary"
                      onClick={() => navigate('/donations/interest-form')}
                      disabled={isProcessing}
                    >
                      ← Back
                    </button>
                    <button
                      className="ul-btn ul-btn-primary"
                      onClick={handlePayment}
                      disabled={isProcessing}
                    >
                      {isProcessing ? `${processingStatus}...` : `Donate $${fees.total.toFixed(2)}`}
                    </button>
                  </div>

                  {isProcessing && (
                    <div className="ul-progress-bar-full" style={{ width: '65%' }}></div>
                  )}
                </div>
              </div>

              {/* Right: Summary Sidebar */}
              <div className="col-lg-4">
                <div className="ul-donation-summary">
                  <h3>Order Summary</h3>

                  <div className="ul-summary-item">
                    <span>Donor:</span>
                    <strong>{donationData.donorName}</strong>
                  </div>

                  <div className="ul-summary-item">
                    <span>Email:</span>
                    <strong>{donationData.donorEmail}</strong>
                  </div>

                  <div className="ul-summary-divider"></div>

                  <div className="ul-summary-item">
                    <span>Donation Amount:</span>
                    <strong>${donationData.amount.toFixed(2)}</strong>
                  </div>

                  <div className="ul-summary-item">
                    <span>Frequency:</span>
                    <strong style={{ textTransform: 'capitalize' }}>
                      {donationData.frequency === 'one-time' ? 'One-Time' : donationData.frequency}
                    </strong>
                  </div>

                  <div className="ul-summary-item">
                    <span>Processor:</span>
                    <strong>{donationData.processor}</strong>
                  </div>

                  <div className="ul-summary-item">
                    <span>Purpose:</span>
                    <strong style={{ textTransform: 'capitalize' }}>
                      {donationData.purpose}
                    </strong>
                  </div>

                  <div className="ul-summary-divider"></div>

                  <div className="ul-fee-breakdown">
                    <div className="ul-fee-item">
                      <span>Subtotal:</span>
                      <span>${donationData.amount.toFixed(2)}</span>
                    </div>
                    <div className="ul-fee-item">
                      <span>Processing Fee:</span>
                      <span>${fees.fees.toFixed(2)}</span>
                    </div>
                    <div className="ul-fee-item ul-fee-total">
                      <span><strong>Total:</strong></span>
                      <span><strong>${fees.total.toFixed(2)}</strong></span>
                    </div>
                  </div>

                  <div className="ul-summary-divider"></div>

                  <div className="ul-trust-badges">
                    <span className="ul-badge">🔒 Secure</span>
                    <span className="ul-badge">✓ Verified</span>
                    <span className="ul-badge">📧 Encrypted</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
};

export default DonationPayment;
